import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Upload, FileArchive, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import authFetch from '../../utils/authFetch';
import useAuthStore from '../../store/authStore';
import { isOrgAdmin } from '../../utils/permissions';

const API = import.meta.env.VITE_API_URL || '';

export default function ImportPage() {
  const user = useAuthStore((s) => s.user);
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceId, setWorkspaceId] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    authFetch(`${API}/api/v1/importer/workspaces`)
      .then((ws) => {
        setWorkspaces(ws);
        if (ws[0]) setWorkspaceId(String(ws[0].id));
      })
      .catch((e) => setError(e.message));
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    if (!file || !workspaceId) return;
    setLoading(true);
    setError(null);
    setReport(null);

    const fd = new FormData();
    fd.append('file', file);
    fd.append('workspace_id', workspaceId);

    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/api/v1/importer/monday`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Falha na importação');
      setReport(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (!isOrgAdmin(user)) return <Navigate to="/" replace />;

  return (
    <div className="p-8 max-w-3xl">
      <h2 className="text-2xl font-semibold text-text-primary mb-2">Importar do Monday</h2>
      <p className="text-text-secondary text-sm mb-6">
        Suba o arquivo <code className="text-accent">.zip</code> exportado do Monday.com.
        Importaremos usuários, boards (com grupos, colunas e cards), updates e anexos.
      </p>

      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Workspace de destino
          </label>
          <select
            value={workspaceId}
            onChange={(e) => setWorkspaceId(e.target.value)}
            className="w-full bg-card border border-border rounded-md px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-accent"
          >
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Arquivo .zip
          </label>
          <label className="flex items-center justify-center gap-3 border-2 border-dashed border-border rounded-lg bg-card px-4 py-10 cursor-pointer hover:border-accent transition-colors">
            <FileArchive className="w-8 h-8 text-text-secondary" />
            <div>
              <div className="text-sm text-text-primary font-medium">
                {file ? file.name : 'Clique para escolher o zip'}
              </div>
              <div className="text-xs text-text-secondary">
                {file ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : 'Máx 10 GB'}
              </div>
            </div>
            <input
              type="file"
              accept=".zip"
              className="hidden"
              onChange={(e) => setFile(e.target.files[0])}
            />
          </label>
        </div>

        <button
          type="submit"
          disabled={!file || !workspaceId || loading}
          className="flex items-center gap-2 bg-accent text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {loading ? 'Importando…' : 'Importar'}
        </button>

        {loading && (
          <div className="text-xs text-text-secondary bg-card border border-border rounded-md p-3">
            A importação roda em uma única transação e pode levar vários minutos dependendo do tamanho do zip.
            Acompanhe o progresso em tempo real pelos logs do servidor: <code className="text-accent">pm2 logs arco-api</code>.
            Não feche esta aba até concluir.
          </div>
        )}
      </form>

      {error && (
        <div className="mt-6 flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-md p-4 text-sm text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}

      {report && (
        <div className="mt-6 bg-card border border-border rounded-md p-5">
          <div className="flex items-center gap-2 text-green-400 mb-4">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-medium">Importação concluída</span>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <Stat label="Usuários criados" value={report.team?.created ?? 0} sub={`${report.team?.skipped ?? 0} já existiam`} />
            <Stat label="Anexos copiados" value={report.assets?.copied ?? 0} />
            <Stat label="Quadros" value={report.boards?.boards ?? 0} sub={`${report.boards?.groups ?? 0} grupos · ${report.boards?.cards ?? 0} cards`} />
            <Stat label="Colunas" value={report.boards?.columns ?? 0} sub={`${report.boards?.values ?? 0} valores`} />
            <Stat label="Updates (comentários)" value={report.updates?.comments ?? 0} sub={`${report.updates?.linked ?? 0} respostas · ${report.updates?.skipped ?? 0} ignorados`} />
          </div>

          <div className="mt-4 text-xs text-text-secondary">
            Arquivos processados: {report.files.boardFiles.length} board(s),
            {report.files.teamFile ? ' team ✓' : ' team ✗'},
            {report.files.updatesFile ? ' updates ✓' : ' updates ✗'},
            {report.files.assetsDir ? ' assets ✓' : ' assets ✗'}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="bg-main rounded-md p-3 border border-border/50">
      <div className="text-xs text-text-secondary mb-1">{label}</div>
      <div className="text-2xl font-semibold text-text-primary">{value}</div>
      {sub && <div className="text-xs text-text-secondary mt-1">{sub}</div>}
    </div>
  );
}
